import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Button Component
 * Reusable button with multiple variants and sizes
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  href,
  to,
  onClick,
  icon,
  iconPosition = 'left',
  className = '',
  ...props
}) => {
  // Base classes
  const baseClasses = 'btn';

  // Variant classes
  const variantClasses = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline',
    'outline-secondary': 'btn-outline-secondary',
    white: 'btn-white',
  };

  // Size classes
  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
  };

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';

  // Disabled/Loading classes
  const stateClasses = disabled || loading ? 'opacity-60 cursor-not-allowed' : '';

  // Combine all classes
  const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant] || ''}
    ${sizeClasses[size] || ''}
    ${widthClasses}
    ${stateClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // Button content with optional icon
  const buttonContent = (
    <>
      {loading && (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!loading && icon && iconPosition === 'left' && (
        <span className="inline-flex">{icon}</span>
      )}
      <span>{children}</span>
      {!loading && icon && iconPosition === 'right' && (
        <span className="inline-flex">{icon}</span>
      )}
    </>
  );

  // If href is provided, render as anchor tag
  if (href) {
    return (
      <a
        href={href}
        className={buttonClasses}
        onClick={disabled || loading ? (e) => e.preventDefault() : onClick}
        {...props}
      >
        {buttonContent}
      </a>
    );
  }

  // If to is provided, render as Link
  if (to) {
    return (
      <Link
        to={to}
        className={buttonClasses}
        onClick={disabled || loading ? (e) => e.preventDefault() : onClick}
        {...props}
      >
        {buttonContent}
      </Link>
    );
  }

  // Otherwise, render as button
  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {buttonContent}
    </button>
  );
};

export default Button;